/**
 * ValidationAlerts
 *
 * Displays validation warnings for the ScenarioEditor:
 * - Missing required fields warning
 * - Inputs changed / rerun recommended warning
 */

import React from 'react';
import { Alert, Typography, Button } from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

/**
 * @param {Object} props
 * @param {boolean} props.showValidation - Whether to show validation warnings
 * @param {Array} props.missingFields - Array of missing required fields
 * @param {number} props.currentTab - Current tab index
 * @param {boolean} props.needsRerun - Whether inputs have changed since last run
 * @param {boolean} props.hasResults - Whether optimization results exist
 * @param {Function} props.onDismissRerun - Callback to dismiss the rerun warning
 */
export default function ValidationAlerts({
    showValidation,
    missingFields,
    currentTab,
    needsRerun,
    hasResults,
    onDismissRerun,
}) {
    return (
        <>
            {/* Validation Warning */}
            {showValidation && missingFields.length > 0 && currentTab === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Required fields missing:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {missingFields.map(f => (
                            <li key={f.key}>{f.label} *</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {/* Inputs Changed Warning - Rerun Recommended */}
            {needsRerun && hasResults && (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        bgcolor: 'rgba(178, 140, 80, 0.2)',
                        color: 'text.primary',
                        border: `1px solid ${vortxColors.gold}`,
                        '& .MuiAlert-icon': { color: vortxColors.gold }
                    }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={onDismissRerun}
                            sx={{ fontWeight: 600 }}
                        >
                            Dismiss
                        </Button>
                    }
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Inputs have changed since last run — Re-run optimization for updated results
                    </Typography>
                </Alert>
            )}
        </>
    );
}
